<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import CautionContainer from "@/components/molecules/CautionContainer.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import { applicationService } from "@/app/services/ApplicationService";
import { Application } from "@/types/order/Application";
import { addMinutes, now, parseISO, subMinutes } from "@/utils/date";
import { useCustomRouter } from "@/utils/router";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";

const route = useRoute();
const loader = useLoader();
const { pushIgnoreError } = useCustomRouter();
const { showErrorModal } = useErrorModal();
const application = ref<Application | null>(null);

const applicationId = computed(() => route.params.applicationId);

const isDiscrepancyStartWorkTime = computed(() => {
  if (!application.value) {
    return false;
  }
  // 予定の開始時間より30分以上早い、または5分以上遅れていれば true
  const startTime = parseISO(application.value.appliedStartWorkAt);
  const nowTime = now();
  return (
    nowTime <= subMinutes(startTime, 30) || nowTime >= addMinutes(startTime, 5)
  );
});

async function onBackApplicationList() {
  await pushIgnoreError({ name: "ApplicationsList" });
}

async function fetchApplication() {
  application.value = await applicationService.fetchApplication(
    Number(applicationId.value)
  );
}

onMounted(async () => {
  try {
    loader.show();
    await fetchApplication();
  } catch (error) {
    showErrorModal(error as Error);
  } finally {
    loader.hide();
  }
});
</script>

<template>
  <div class="work-started">
    <div class="content">
      <div class="description">
        <h3 class="description-title">出勤処理が完了しました</h3>
        <div class="description-content">
          <p>お仕事頑張ってください！</p>
        </div>
        <div class="description-image">
          <img
            src="@/assets/img/caikuma_jumping.svg?webp"
            alt="カイクマ"
            class="caikuma"
          />
        </div>
      </div>
      <CautionContainer
        v-if="isDiscrepancyStartWorkTime"
        data-testid="work-time-caution"
      >
        <template #title>出勤時間にズレがあります</template>
        <template #content>
          <p>退勤後に勤怠変更申請が必要な可能性があります</p>
          <p>退勤後、画面に沿って操作をお願いします</p>
        </template>
      </CautionContainer>
    </div>
    <div class="bottom">
      <AppButton block @click="onBackApplicationList">
        仕事管理に戻る
      </AppButton>
    </div>
    <BlockBack />
  </div>
</template>

<style lang="scss" scoped>
.work-started {
  display: flex;
  flex-direction: column;
  height: 100dvh;
}

.content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 40px;
  align-items: center;
  justify-content: center;
  padding-top: calc(env(safe-area-inset-top) + 24px);
  overflow: auto;
}

.description {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 100%;
  padding: 24px 24px 0;

  .description-title {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    line-height: 36px;
    color: $orange-70;
    letter-spacing: 0;
  }

  .description-content {
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #31373f;
    text-align: center;
    letter-spacing: 0;
  }

  .description-image {
    padding-top: 32px;
  }
}

.caikuma {
  width: 139px;
  height: auto;
}

.bottom {
  display: flex;
  justify-content: center;
  padding: 24px 24px calc(env(safe-area-inset-bottom) + 24px) 24px;
  border-top: 1px solid #e5e5e5;

  button {
    width: 100%;
    font-size: 16px;
  }
}
</style>
